import React, { useRef, useState, useMemo, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Phone, ArrowUpRight, ArrowDown, Check, LayoutPanelTop, PaintRoller,
  Layers, Truck, MessageCircle, ShieldCheck, Clock, Award, Handshake, Sparkles,
  LayoutGrid, DoorOpen, Building2, Hammer, SprayCan, Info, Zap, Droplet, X, ChevronLeft, ChevronRight,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./components/ui/select";
import { PHONES, WHATSAPP, IMAGES, SERVICE_GROUPS, LOGO, GALLERY, GALLERY_CATS } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GROUP_ICONS = { LayoutPanelTop, PaintRoller, Layers, Truck, LayoutGrid, DoorOpen, Building2, Hammer, SprayCan, Zap, Droplet };
const WHY_ICONS = [Award, Handshake, Clock, ShieldCheck];

const ease = [0.22, 1, 0.36, 1];

// Masked line reveal
export const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className="reveal-mask">
    <motion.span
      className={className}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, ease, delay }}
      style={{ display: "block" }}
    >
      {children}
    </motion.span>
  </span>
);

const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, ease, delay }}
  >
    {children}
  </motion.div>
);

const Overline = ({ children }) => (
  <div className="flex items-center gap-3 mb-5">
    <span className="h-[2px] w-10 bg-copper" />
    <span className="font-body text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-copper">
      {children}
    </span>
  </div>
);

/* ---------------- HERO ---------------- */
export const Hero = ({ t, scrollTo }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} id="hero" className="relative min-h-screen bg-navy text-white overflow-hidden grain" data-testid="hero-section">
      {/* Background image clipped */}
      <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute right-0 top-0 h-full w-full md:w-[52%] clip-arch-2 overflow-hidden">
        <img src={IMAGES.hero} alt="Интериор" className="h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/40 to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-24">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
          <Overline>{t.hero.overline}</Overline>
        </motion.div>

        <h1 className="font-heading font-bold uppercase leading-[0.92] tracking-tight text-6xl sm:text-7xl md:text-8xl lg:text-9xl">
          <RevealLine delay={0.15}>{t.hero.l1}</RevealLine>
          <RevealLine delay={0.3} className="text-copper">{t.hero.l2}</RevealLine>
          <RevealLine delay={0.45}>{t.hero.l3}</RevealLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.7 }}
          className="mt-8 font-body text-lg md:text-2xl text-white/80 max-w-xl"
        >
          {t.hero.sub}
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.7 }}
          className="mt-3 font-body italic text-copper text-base md:text-lg"
        >
          „{t.hero.motto}"
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <Button
            data-testid="hero-pricing-btn"
            onClick={() => scrollTo("services")}
            className="rounded-none bg-copper hover:bg-copper-hover text-white font-body font-semibold uppercase tracking-wider px-8 h-14 text-sm transition-colors"
          >
            {t.hero.cta1} <ArrowUpRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            data-testid="hero-contact-btn"
            onClick={() => scrollTo("contact")}
            variant="outline"
            className="rounded-none bg-transparent border-2 border-white/40 hover:border-copper hover:text-copper text-white font-body font-semibold uppercase tracking-wider px-8 h-14 text-sm transition-colors"
          >
            {t.hero.cta2}
          </Button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-6 md:left-10 z-10 flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest"
      >
        <ArrowDown className="h-4 w-4 animate-bounce" /> Scroll
      </motion.div>
    </section>
  );
};

/* ---------------- MARQUEE ---------------- */
export const Ribbon = ({ t }) => (
  <div className="bg-copper py-4 border-y-2 border-navy overflow-hidden" data-testid="marquee-ribbon">
    <Marquee speed={40} gradient={false} autoFill>
      <span className="font-heading uppercase text-navy text-2xl md:text-3xl font-semibold tracking-wide mx-8">
        {t.marquee} •
      </span>
    </Marquee>
  </div>
);

/* ---------------- ABOUT ---------------- */
export const About = ({ t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  return (
    <section ref={ref} id="about" className="relative bg-white py-24 md:py-36" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <Overline>{t.about.overline}</Overline>
            <FadeUp>
              <h2 className="font-heading uppercase font-bold tracking-tight text-4xl md:text-6xl text-navy leading-[0.95]">
                {t.about.title}
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-8 font-body text-lg text-steel leading-relaxed max-w-lg">{t.about.lead}</p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="mt-8 inline-flex items-center gap-3 bg-navy text-white px-6 py-4">
                <Sparkles className="h-5 w-5 text-copper" />
                <span className="font-body font-semibold uppercase tracking-wide text-sm">{t.about.spec}</span>
              </div>
            </FadeUp>
            <div className="mt-12 relative h-64 md:h-80 overflow-hidden clip-arch">
              <motion.img style={{ y }} src={IMAGES.about} alt="Качество" className="w-full h-[130%] object-cover" />
            </div>
          </div>

          <div className="lg:pt-16">
            {t.about.chapters.map((c, i) => (
              <FadeUp key={c.n} delay={i * 0.08}>
                <div className="group flex gap-6 py-8 border-t border-mist hover:border-copper transition-colors">
                  <span className="font-heading font-bold text-5xl md:text-6xl text-mist group-hover:text-copper transition-colors leading-none">
                    {c.n}
                  </span>
                  <div>
                    <h3 className="font-heading uppercase font-semibold text-xl md:text-2xl text-navy tracking-tight">{c.t}</h3>
                    <p className="mt-2 font-body text-steel leading-relaxed">{c.d}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- SERVICES ---------------- */
export const Services = ({ t }) => {
  const unitLabel = (u) =>
    u === "sqm" ? t.services.perSqm : u === "piece" ? t.services.perPiece : u === "meter" ? t.services.perMeter : u === "flat" ? t.services.flatUnit : "";
  const priceLabel = (item) => {
    if (item.price === null) {
      return ["figures", "led"].includes(item.id) ? t.services.onRequest : t.services.onRequest2;
    }
    return `${item.price} ${unitLabel(item.unit)}`;
  };
  return (
    <section id="services" className="relative bg-navy text-white py-24 md:py-36 grain" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Overline>{t.services.overline}</Overline>
        <FadeUp>
          <h2 className="font-heading uppercase font-bold tracking-tight text-4xl md:text-6xl leading-[0.95] max-w-2xl">
            {t.services.title}
          </h2>
        </FadeUp>

        <div className="mt-16 grid md:grid-cols-2 gap-x-16 gap-y-16">
          {SERVICE_GROUPS.map((g, gi) => {
            const Icon = GROUP_ICONS[g.icon];
            return (
              <FadeUp key={g.key} delay={(gi % 2) * 0.05}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center justify-center h-12 w-12 bg-copper text-navy shrink-0">
                    <Icon className="h-6 w-6" strokeWidth={1.8} />
                  </div>
                  <h3 className="font-heading uppercase font-semibold text-xl md:text-2xl tracking-tight text-copper leading-tight">
                    {t.services.groups[g.key]}
                  </h3>
                </div>
                <div>
                  {g.items.map((item) => (
                    <div
                      key={item.id}
                      data-testid={`service-row-${item.id}`}
                      className="group flex items-baseline justify-between gap-4 py-4 border-t border-white/10 hover:border-copper transition-colors"
                    >
                      <span className="font-body text-white/85 group-hover:text-white transition-colors leading-snug">
                        {t.services.items[item.id]}
                      </span>
                      <span className={`font-heading whitespace-nowrap text-right ${item.price === null ? "text-white/50 text-sm italic" : "text-copper text-lg md:text-xl font-semibold"}`}>
                        {priceLabel(item)}
                      </span>
                    </div>
                  ))}
                  {g.note && (
                    <p className="mt-3 font-body text-xs text-white/45 italic">{t.services.tilesNote}</p>
                  )}
                </div>
              </FadeUp>
            );
          })}
        </div>

        {/* Additional conditions */}
        <FadeUp>
          <div className="mt-20 border border-copper/40">
            <div className="flex items-center gap-3 bg-copper text-navy px-6 py-4">
              <Info className="h-5 w-5" />
              <h3 className="font-heading uppercase font-semibold text-lg tracking-wide">{t.services.conditions.title}</h3>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
              {t.services.conditions.items.map((c) => (
                <div key={c.t} data-testid={`condition-${c.t}`} className="p-6">
                  <div className="font-heading text-3xl font-bold text-copper">{c.v}</div>
                  <div className="mt-2 font-body text-sm text-white/70 leading-snug">{c.t}</div>
                </div>
              ))}
            </div>
          </div>
        </FadeUp>

        <FadeUp>
          <p className="mt-10 font-body text-sm text-white/50 max-w-2xl border-l-2 border-copper pl-4">{t.services.note}</p>
        </FadeUp>
      </div>
    </section>
  );
};

/* ---------------- CALCULATOR ---------------- */
export const Calculator = ({ t, scrollTo }) => {
  const options = useMemo(
    () => SERVICE_GROUPS.flatMap((g) => g.items).filter((i) => typeof i.price === "number" && i.unit === "sqm"),
    []
  );
  const [service, setService] = useState("");
  const [area, setArea] = useState("");

  const selected = options.find((o) => o.id === service);
  const total = selected && area && !isNaN(parseFloat(area))
    ? (selected.price * parseFloat(area)).toFixed(2)
    : null;

  return (
    <section id="calculator" className="bg-white py-24 md:py-36" data-testid="calculator-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <Overline>{t.calc.overline}</Overline>
          <FadeUp>
            <h2 className="font-heading uppercase font-bold tracking-tight text-4xl md:text-6xl text-navy leading-[0.95]">
              {t.calc.title}
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-6 font-body text-lg text-steel max-w-md">{t.calc.sub}</p>
          </FadeUp>
        </div>

        <FadeUp delay={0.15}>
          <div className="bg-navy text-white p-8 md:p-10 clip-arch">
            <div className="space-y-6">
              <div>
                <Label className="font-body uppercase tracking-wider text-xs text-copper mb-2 block">{t.calc.service}</Label>
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger data-testid="calc-service-select" className="rounded-none bg-white/5 border-white/20 text-white h-12 font-body">
                    <SelectValue placeholder={t.calc.servicePh} />
                  </SelectTrigger>
                  <SelectContent>
                    {options.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {t.services.items[o.id]} — {o.price} {t.services.perSqm}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-body uppercase tracking-wider text-xs text-copper mb-2 block">{t.calc.area}</Label>
                <Input
                  data-testid="calc-area-input"
                  type="number" min="0" value={area}
                  onChange={(e) => setArea(e.target.value)}
                  placeholder={t.calc.areaPh}
                  className="rounded-none bg-white/5 border-white/20 text-white h-12 font-body placeholder:text-white/30"
                />
              </div>

              <div className="border-t border-white/10 pt-6">
                <span className="font-body uppercase tracking-wider text-xs text-white/50">{t.calc.result}</span>
                <div data-testid="calc-result" className="font-heading font-bold text-5xl md:text-6xl text-copper mt-2">
                  {total ? `${total} €` : "— €"}
                </div>
                <p className="mt-3 text-xs text-white/40 font-body">{t.calc.disclaimer}</p>
              </div>

              <Button
                data-testid="calc-cta-btn"
                onClick={() => scrollTo("contact")}
                className="w-full rounded-none bg-copper hover:bg-copper-hover text-white font-body font-semibold uppercase tracking-wider h-14 transition-colors"
              >
                {t.calc.cta} <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

/* ---------------- WHY US ---------------- */
export const WhyUs = ({ t }) => (
  <section id="why" className="bg-[#F8F9FA] py-24 md:py-36" data-testid="why-section">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <Overline>{t.why.overline}</Overline>
      <FadeUp>
        <h2 className="font-heading uppercase font-bold tracking-tight text-4xl md:text-6xl text-navy leading-[0.95] max-w-2xl">
          {t.why.title}
        </h2>
      </FadeUp>
      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {t.why.items.map((it, i) => {
          const Icon = WHY_ICONS[i];
          const dark = i % 2 === 1;
          return (
            <FadeUp key={it.t} delay={i * 0.08}>
              <div
                data-testid={`why-card-${i}`}
                className={`h-full p-8 border transition-all duration-300 hover:-translate-y-1 ${dark ? "bg-navy border-navy text-white" : "bg-white border-mist hover:border-copper"}`}
              >
                <Icon className="h-9 w-9 text-copper mb-6" strokeWidth={1.6} />
                <h3 className={`font-heading uppercase font-semibold text-xl tracking-tight ${dark ? "text-white" : "text-navy"}`}>{it.t}</h3>
                <p className={`mt-3 font-body text-sm leading-relaxed ${dark ? "text-white/70" : "text-steel"}`}>{it.d}</p>
                <Check className="h-5 w-5 text-copper mt-6" />
              </div>
            </FadeUp>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- CONTACT ---------------- */
export const Contact = ({ t }) => {
  const [form, setForm] = useState({ name: "", phone: "", service: "", message: "" });
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const options = useMemo(() => SERVICE_GROUPS.flatMap((g) => g.items), []);

  const submit = (e) => {
    e.preventDefault();
    setStatus(null);
    if (!form.name.trim() || !form.phone.trim()) {
      setStatus("error");
      toast.error(t.contact.error);
      return;
    }
    // Build a pre-filled WhatsApp message to Petar Iliev (PHONES[0])
    const lines = [
      "Ново запитване от сайта P&P Construction",
      `Име: ${form.name}`,
      `Телефон: ${form.phone}`,
      form.service ? `Услуга: ${form.service}` : "",
      form.message ? `Съобщение: ${form.message}` : "",
    ].filter(Boolean).join("\n");
    const waUrl = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(lines)}`;
    // Open WhatsApp first (synchronous, within the click gesture so it isn't blocked)
    window.open(waUrl, "_blank");
    // Keep a best-effort record in the database (non-blocking)
    axios.post(`${API}/inquiries`, form).catch((err) => console.error("DB save failed:", err?.message));
    toast.success(t.contact.success);
    setStatus("success");
    setForm({ name: "", phone: "", service: "", message: "" });
    if (typeof document !== "undefined" && document.activeElement?.blur) document.activeElement.blur();
  };

  return (
    <section id="contact" className="bg-navy text-white grain" data-testid="contact-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2">
        {/* Info */}
        <div className="p-8 md:p-16 lg:p-20 flex flex-col justify-center">
          <Overline>{t.contact.overline}</Overline>
          <h2 className="font-heading uppercase font-bold tracking-tight text-4xl md:text-6xl leading-[0.95]">{t.contact.title}</h2>
          <p className="mt-6 font-body text-lg text-white/70 max-w-md">{t.contact.lead}</p>

          <div className="mt-10 space-y-4">
            {PHONES.map((p) => (
              <a
                key={p.tel} href={`tel:${p.tel}`} data-testid={`contact-phone-${p.tel}`}
                className="group flex items-center gap-4 border border-white/15 hover:border-copper p-5 transition-colors"
              >
                <div className="flex items-center justify-center h-12 w-12 bg-copper text-navy group-hover:scale-105 transition-transform">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-heading text-2xl tracking-wide">{p.display}</div>
                  <div className="font-body text-xs uppercase tracking-widest text-white/50">{p.name}</div>
                </div>
              </a>
            ))}
            <a
              href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn"
              className="flex items-center gap-3 text-copper hover:text-white transition-colors font-body font-semibold uppercase tracking-wider text-sm pt-2"
            >
              <MessageCircle className="h-5 w-5" /> {t.contact.whatsapp}
            </a>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white text-navy p-8 md:p-16 lg:p-20">
          <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
            {status === "success" && (
              <div data-testid="contact-success-msg" className="flex items-start gap-3 bg-green-50 border border-green-500 text-green-800 p-4">
                <Check className="h-5 w-5 mt-0.5 shrink-0" />
                <span className="font-body text-sm">{t.contact.success}</span>
              </div>
            )}
            {status === "error" && (
              <div data-testid="contact-error-msg" className="flex items-start gap-3 bg-red-50 border border-red-500 text-red-700 p-4">
                <Info className="h-5 w-5 mt-0.5 shrink-0" />
                <span className="font-body text-sm">{t.contact.error}</span>
              </div>
            )}
            <div>
              <Label className="font-body uppercase tracking-wider text-xs text-steel mb-2 block">{t.contact.name} *</Label>
              <Input data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t.contact.namePh} className="rounded-none border-mist h-12 font-body" />
            </div>
            <div>
              <Label className="font-body uppercase tracking-wider text-xs text-steel mb-2 block">{t.contact.phone} *</Label>
              <Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder={t.contact.phonePh} className="rounded-none border-mist h-12 font-body" />
            </div>
            <div>
              <Label className="font-body uppercase tracking-wider text-xs text-steel mb-2 block">{t.contact.service}</Label>
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger data-testid="contact-service-select" className="rounded-none border-mist h-12 font-body">
                  <SelectValue placeholder={t.contact.servicePh} />
                </SelectTrigger>
                <SelectContent>
                  {options.map((o) => (
                    <SelectItem key={o.id} value={t.services.items[o.id]}>{t.services.items[o.id]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-body uppercase tracking-wider text-xs text-steel mb-2 block">{t.contact.message}</Label>
              <Textarea data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t.contact.messagePh} rows={4} className="rounded-none border-mist font-body resize-none" />
            </div>
            <Button
              type="submit" data-testid="contact-submit-btn"
              className="w-full rounded-none bg-[#25D366] hover:bg-[#1da851] text-white font-body font-semibold uppercase tracking-wider h-14 transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="h-5 w-5" /> {t.contact.submit}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

/* ---------------- GALLERY ---------------- */
export const Gallery = ({ t }) => {
  const [idx, setIdx] = useState(null);
  const order = { drywall: 0, tiles: 1, concrete: 2 };
  const items = useMemo(
    () => GALLERY
      .map((src, i) => ({ src, cat: GALLERY_CATS[i] }))
      .sort((a, b) => (order[a.cat] ?? 9) - (order[b.cat] ?? 9)),
    []
  );

  const open = idx !== null;
  const close = () => setIdx(null);
  const prev = () => setIdx((v) => (v - 1 + items.length) % items.length);
  const next = () => setIdx((v) => (v + 1) % items.length);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, items.length]);

  return (
    <section id="gallery" className="bg-[#F8F9FA] py-24 md:py-36" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Overline>{t.gallery.overline}</Overline>
        <FadeUp>
          <h2 className="font-heading uppercase font-bold tracking-tight text-4xl md:text-6xl text-navy leading-[0.95]">
            {t.gallery.title}
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="mt-6 font-body text-lg text-steel max-w-2xl">{t.gallery.sub}</p>
        </FadeUp>

        <div className="mt-14 [column-fill:_balance] columns-2 md:columns-3 lg:columns-4 gap-4">
          {items.map((it, i) => (
            <motion.button
              key={it.src}
              data-testid={`gallery-item-${i}`}
              onClick={() => setIdx(i)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, ease }}
              className="group mb-4 block w-full overflow-hidden bg-mist relative"
            >
              <img
                src={it.src} alt={`Проект ${i + 1}`} loading="lazy"
                className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute inset-0 ring-0 group-hover:ring-2 ring-copper transition-all" />
            </motion.button>
          ))}
        </div>
      </div>

      {open && (
        <div
          data-testid="gallery-lightbox"
          onClick={close}
          className="fixed inset-0 z-[60] bg-navy/95 flex flex-col items-center justify-center p-4 sm:p-8"
        >
          <button data-testid="gallery-close" onClick={close}
            className="absolute top-5 right-5 text-white hover:text-copper transition-colors z-10">
            <X className="h-9 w-9" />
          </button>

          <div className="flex-1 min-h-0 w-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <motion.img
              key={items[idx].src}
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }}
              src={items[idx].src} alt="Проект" className="max-h-[76vh] max-w-[92vw] object-contain shadow-2xl"
            />
          </div>

          {/* Controls under the image */}
          <div className="mt-6 flex items-center gap-6" onClick={(e) => e.stopPropagation()}>
            <button data-testid="gallery-prev" onClick={prev} aria-label="Предишна"
              className="flex items-center justify-center h-14 w-14 border-2 border-white/30 text-white hover:bg-copper hover:border-copper transition-colors">
              <ChevronLeft className="h-7 w-7" />
            </button>
            <span data-testid="gallery-counter" className="font-heading text-white text-xl tracking-widest tabular-nums">
              {idx + 1} / {items.length}
            </span>
            <button data-testid="gallery-next" onClick={next} aria-label="Следваща"
              className="flex items-center justify-center h-14 w-14 border-2 border-white/30 text-white hover:bg-copper hover:border-copper transition-colors">
              <ChevronRight className="h-7 w-7" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

/* ---------------- FOOTER ---------------- */
export const Footer = ({ t }) => (
  <footer className="bg-navy border-t border-white/10 text-white py-14" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-4">
        <div className="bg-white p-2">
          <img src={LOGO} alt="P&P Construction" className="h-10 w-auto" />
        </div>
        <span className="font-heading uppercase tracking-wide text-copper text-lg">{t.footer.slogan}</span>
      </div>
      <div className="font-body text-sm text-white/40">
        © {new Date().getFullYear()} P&P Construction. {t.footer.rights}
      </div>
    </div>
  </footer>
);

/* ---------------- FLOATING ---------------- */
export const Floating = ({ t }) => (
  <>
    <a
      href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer" data-testid="floating-whatsapp"
      className="fixed bottom-6 right-6 z-50 flex items-center justify-center h-14 w-14 bg-[#25D366] text-white shadow-lg hover:scale-110 transition-transform"
      aria-label="WhatsApp"
    >
      <MessageCircle className="h-7 w-7" />
    </a>
    <a
      href={`tel:${PHONES[0].tel}`} data-testid="floating-call"
      className="fixed bottom-6 left-6 z-50 md:hidden flex items-center gap-2 bg-copper text-white px-5 h-14 font-body font-semibold uppercase tracking-wider text-sm shadow-lg"
    >
      <Phone className="h-5 w-5" /> {t.nav.call}
    </a>
  </>
);
